/**
 * Apostille / MFA-legalisation comparison cluster — /apostille/compare/{slug}.
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-04)
 * - ไทยยังไม่ออกตรา Apostille — อนุสัญญามีผลกับไทย 28 ก.พ. 2027 (hcch.net · mfa.go.th)
 * - นิติกรณ์: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
 *   200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import { APOSTILLE_STATUS } from "@/data/legal-facts";

export interface ApostilleCompareRow {
  aspect: string;
  a: string;
  b: string;
}

export interface ApostilleCompare {
  slug: string;
  title: string;
  description: string;
  h1: string;
  aLabel: string;
  bLabel: string;
  answer: string;
  rows: ApostilleCompareRow[];
  chooseA: string[];
  chooseB: string[];
  faqs: Array<{ q: string; a: string }>;
  synonyms: string[];
}

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — เงื่อนไขของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรายการเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

const FEE =
  "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลคือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน (consular.mfa.go.th)";

const QUOTE =
  "เว็บไซต์ไม่แสดงราคาบริการ — สอบถามค่าบริการรายเคสทางโทรศัพท์ LINE หรืออีเมล";

export const APOSTILLE_COMPARE: ApostilleCompare[] = [
  {
    slug: "apostille-vs-legalization",
    title: "Apostille กับ Legalization ต่างกันอย่างไร และไทยใช้แบบไหน",
    description:
      "เทียบตรา Apostille ชั้นเดียวตามอนุสัญญากรุงเฮก กับการรับรองแบบหลายชั้น (นิติกรณ์กรมการกงสุล + สถานทูตปลายทาง) พร้อมสถานะปัจจุบันของประเทศไทย",
    h1: "Apostille vs Legalization: ต่างกันที่จำนวนชั้นการรับรอง",
    aLabel: "Apostille",
    bLabel: "Legalization (นิติกรณ์ + สถานทูต)",
    answer: `Apostille เป็นตรารับรองชั้นเดียวที่ประเทศภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 ยอมรับร่วมกัน ส่วน Legalization ต้องผ่านนิติกรณ์ของประเทศต้นทางแล้วประทับตราสถานทูตปลายทางอีกชั้น ปัจจุบันเอกสารไทยยังใช้เส้นทาง Legalization เพราะอนุสัญญาจะมีผลกับไทยวันที่ ${APOSTILLE_STATUS.inForceDateTh}`,
    rows: [
      { aspect: "จำนวนชั้น", a: "ชั้นเดียวจากหน่วยงานผู้มีอำนาจ", b: "สองชั้นขึ้นไป (นิติกรณ์ + สถานทูต)" },
      { aspect: "สถานะในไทยตอนนี้", a: `ยังออกไม่ได้จนถึง ${APOSTILLE_STATUS.inForceDateTh}`, b: "ใช้อยู่จริงในปัจจุบัน" },
      { aspect: "ประเทศที่รับ", a: "เฉพาะประเทศภาคีอนุสัญญา", b: "ใช้ได้ทั้งประเทศภาคีและนอกภาคี" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "กำหนดโดยหน่วยงานผู้ออกในแต่ละประเทศ", b: "200/400 บาทต่อตราประทับที่กรมการกงสุล + ค่าธรรมเนียมสถานทูต" },
      { aspect: "ระยะเวลาโดยรวม", a: "สั้นกว่าเพราะลดขั้นสถานทูต", b: "ยาวกว่าเพราะรอคิวสองหน่วยงาน" },
      { aspect: "คำแปล", a: "ปลายทางกำหนดว่าต้องแปลหรือไม่", b: "ต้องแปลก่อนเข้าขั้นรับรองในกรณีส่วนใหญ่" },
    ],
    chooseA: [
      "เอกสารออกในประเทศที่เป็นภาคีอนุสัญญาแล้ว",
      "ปลายทางระบุชัดว่ารับ Apostille",
      `วางแผนยื่นหลังวันที่ ${APOSTILLE_STATUS.inForceDateTh} สำหรับเอกสารไทย`,
    ],
    chooseB: [
      "เอกสารออกในไทยและต้องยื่นก่อนอนุสัญญามีผล",
      "ปลายทางเป็นประเทศนอกภาคีอนุสัญญา",
      "สถานทูตปลายทางระบุให้ผ่านนิติกรณ์ก่อนจึงรับเรื่อง",
    ],
    faqs: [
      { q: "ตอนนี้ขอ Apostille จากกรมการกงสุลได้ไหม", a: `ยังไม่ได้ ไทยยื่นภาคยานุวัติสารเมื่อ ${APOSTILLE_STATUS.accessionDateTh} และอนุสัญญาจะมีผลกับไทยวันที่ ${APOSTILLE_STATUS.inForceDateTh} ระหว่างนี้กรมการกงสุลให้บริการนิติกรณ์ตามเดิม ถ้าปลายทางระบุคำว่า Apostille ควรแจ้งข้อเท็จจริงนี้และขอให้ยืนยันรูปแบบที่ยอมรับ ${TAIL}` },
      { q: "ถ้ายื่นตอนนี้แล้วจะต้องทำใหม่หลังปี 2027 ไหม", a: `เอกสารที่ผ่านการรับรองครบชั้นและถูกใช้ตามวัตถุประสงค์แล้วไม่ต้องทำใหม่ แต่ถ้าปลายทางกำหนดอายุเอกสาร 3-6 เดือนและคุณยื่นช้ากว่านั้น ต้องคัดต้นฉบับและรับรองใหม่อยู่ดี ${TAIL}` },
      { q: "ค่าใช้จ่ายต่างกันมากไหม", a: `เส้นทางปัจจุบันมีค่าธรรมเนียมสองส่วนคือของกรมการกงสุลและของสถานทูตปลายทาง ${FEE} ส่วนค่าธรรมเนียมสถานทูตแต่ละแห่งกำหนดเอง ${QUOTE} ${TAIL}` },
      { q: "เอกสารเอกชนใช้เส้นทางไหน", a: `ทั้งสองเส้นทางกำหนดให้เอกสารเอกชน เช่น หนังสือมอบอำนาจหรือ Affidavit ต้องมีการรับรองลายมือชื่อโดยผู้มีอำนาจก่อน จึงจะเข้าสู่สายรับรองของรัฐได้ ${TAIL}` },
    ],
    synonyms: ["apostille vs legalization", "ต่างกัน apostille นิติกรณ์", "which certification do i need thailand"],
  },
  {
    slug: "mfa-vs-embassy",
    title: "นิติกรณ์กรมการกงสุล กับ ตราสถานทูตปลายทาง ต้องทำอันไหนก่อน",
    description:
      "เทียบหน้าที่ของนิติกรณ์กระทรวงการต่างประเทศกับการประทับตราของสถานทูตปลายทาง พร้อมลำดับที่ถูกต้องและจุดที่ทำให้เอกสารถูกตีกลับ",
    h1: "นิติกรณ์กรมการกงสุล vs ตราสถานทูตปลายทาง",
    aLabel: "นิติกรณ์กรมการกงสุล",
    bLabel: "ตราสถานทูตปลายทาง",
    answer:
      "นิติกรณ์เป็นการยืนยันว่าลายมือชื่อและตราของหน่วยงานไทยบนเอกสารเป็นของจริง ส่วนตราสถานทูตเป็นการยืนยันของประเทศปลายทางว่ารับรู้การรับรองของไทย ลำดับที่ถูกต้องคือนิติกรณ์ก่อน แล้วจึงยื่นสถานทูต",
    rows: [
      { aspect: "ผู้ให้บริการ", a: "กองสัญชาติและนิติกรณ์ กรมการกงสุล", b: "สถานเอกอัครราชทูตหรือสถานกงสุลของประเทศปลายทาง" },
      { aspect: "สิ่งที่รับรอง", a: "ความแท้จริงของลายมือชื่อและตราหน่วยงานไทย", b: "การยอมรับเอกสารเพื่อใช้ในประเทศตน" },
      { aspect: "ค่าธรรมเนียม", a: "200 บาท/ตราประทับ ปกติ · 400 บาท/ตราประทับ ด่วน", b: "แต่ละสถานทูตกำหนดเอง" },
      { aspect: "ลำดับ", a: "ทำก่อน", b: "ทำหลัง" },
      { aspect: "จำเป็นเสมอไหม", a: "จำเป็นสำหรับเอกสารไทยที่ใช้ต่างประเทศเกือบทุกกรณี", b: "เฉพาะเมื่อปลายทางกำหนด" },
      { aspect: "ภาษาคำแปล", a: "ปกติรับคำแปลภาษาอังกฤษ", b: "บางแห่งขอภาษาราชการของตน" },
    ],
    chooseA: [
      "เพิ่งเตรียมต้นฉบับและคำแปลเสร็จ",
      "ยังไม่ทราบว่าปลายทางขอตราสถานทูตหรือไม่",
      "ต้องใช้เอกสารกับหลายประเทศจากชุดเดียวกัน",
    ],
    chooseB: [
      "เช็กลิสต์ปลายทางระบุตราสถานทูตไว้ชัดเจน",
      "ผ่านนิติกรณ์เรียบร้อยแล้ว",
      "ปลายทางขอคำแปลเป็นภาษาราชการของประเทศนั้น",
    ],
    faqs: [
      { q: "ข้ามขั้นนิติกรณ์ไปยื่นสถานทูตเลยได้ไหม", a: `โดยทั่วไปไม่ได้ เพราะสถานทูตปลายทางรับรองต่อจากตราของกระทรวงการต่างประเทศไทย ถ้าไม่มีตรานี้ สถานทูตมักส่งเรื่องกลับให้ไปดำเนินการก่อน ${TAIL}` },
      { q: "ต้องนัดคิวสถานทูตล่วงหน้าไหม", a: `หลายสถานทูตเปิดรับเฉพาะช่วงเวลาและต้องจองคิวออนไลน์ล่วงหน้า จึงควรตรวจหน้าเว็บทางการของสถานทูตนั้นและจองคิวคู่ขนานไปกับการยื่นนิติกรณ์ เพื่อไม่ให้เอกสารพร้อมแต่ไม่มีคิว ${TAIL}` },
      { q: "รับรองที่กรมการกงสุลแล้วใช้ได้กี่ประเทศ", a: `ตราของกรมการกงสุลใช้เป็นฐานสำหรับหลายประเทศได้ แต่ถ้าปลายทางต้องประทับตราสถานทูต ต้องยื่นแยกรายประเทศ และหลายแห่งไม่รับสำเนาจากชุดที่รับรองแล้ว จึงควรขอรับรองจำนวนชุดตามที่ต้องใช้ตั้งแต่แรก ${FEE} ${TAIL}` },
      { q: "ค่าบริการดำเนินการแทนคิดอย่างไร", a: `${QUOTE} ทีมงานประเมินจากจำนวนฉบับ ภาษา จำนวนหน่วยงานที่ต้องเดินเรื่อง และความเร่งด่วน โดยค่าธรรมเนียมราชการแสดงแยกจากค่าบริการเสมอ ${TAIL}` },
    ],
    synonyms: ["นิติกรณ์กับสถานทูต", "mfa or embassy first", "ลำดับรับรองเอกสารต่างประเทศ"],
  },
  {
    slug: "notary-vs-mfa",
    title: "ทนายรับรองลายมือชื่อ กับ นิติกรณ์กรมการกงสุล ใช้แทนกันได้ไหม",
    description:
      "เทียบบทบาทของทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร กับการรับรองนิติกรณ์ของกรมการกงสุล ว่าเอกสารเอกชนต้องผ่านขั้นไหนก่อน",
    h1: "ทนายรับรองลายมือชื่อ vs นิติกรณ์กรมการกงสุล",
    aLabel: "ทนายผู้ทำคำรับรองลายมือชื่อ",
    bLabel: "นิติกรณ์กรมการกงสุล",
    answer:
      "ใช้แทนกันไม่ได้ เพราะทำคนละหน้าที่ ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองว่าผู้ลงนามลงนามต่อหน้าจริง ส่วนนิติกรณ์รับรองความแท้จริงของลายมือชื่อและตราบนเอกสารเพื่อให้ใช้ต่างประเทศได้ เอกสารเอกชนต้องผ่านทนายก่อนจึงเข้าสายนิติกรณ์",
    rows: [
      { aspect: "ผู้ให้บริการ", a: "ทนายความที่ขึ้นทะเบียนกับสภาทนายความ", b: "กองสัญชาติและนิติกรณ์ กรมการกงสุล" },
      { aspect: "สิ่งที่รับรอง", a: "ตัวตนผู้ลงนามและการลงนามต่อหน้า", b: "ความแท้จริงของลายมือชื่อและตราหน่วยงาน" },
      { aspect: "ประเภทเอกสาร", a: "เอกสารเอกชน เช่น หนังสือมอบอำนาจ Affidavit สัญญา", b: "เอกสารราชการ และเอกสารเอกชนที่ผ่านการรับรองแล้ว" },
      { aspect: "ต้องมาด้วยตนเองไหม", a: "ผู้ลงนามต้องมาลงนามต่อหน้าพร้อมบัตรหรือพาสปอร์ตตัวจริง", b: "ยื่นแทนได้ตามเงื่อนไขของหน่วยงาน" },
      { aspect: "ลำดับ", a: "ทำก่อน", b: "ทำหลัง" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "ไม่มีค่าธรรมเนียมราชการในขั้นนี้", b: "200/400 บาทต่อตราประทับ" },
    ],
    chooseA: [
      "เอกสารเป็นเอกสารเอกชนที่ผู้ลงนามจัดทำเอง",
      "ปลายทางขอให้มีผู้รับรองลายมือชื่อ",
      "ต้องใช้เอกสารกับคู่สัญญาต่างประเทศโดยตรง",
    ],
    chooseB: [
      "เอกสารเป็นเอกสารราชการที่คัดจากหน่วยงานผู้ออก",
      "ต้องนำเอกสารไปใช้กับหน่วยงานรัฐต่างประเทศ",
      "สถานทูตปลายทางกำหนดให้ผ่านกระทรวงการต่างประเทศก่อน",
    ],
    faqs: [
      { q: "เอกสารราชการต้องผ่านทนายก่อนไหม", a: `ไม่ต้อง เอกสารราชการที่คัดใหม่จากหน่วยงานผู้ออก เช่น สูติบัตร ทะเบียนบ้าน หรือทะเบียนสมรส เข้าสู่ขั้นนิติกรณ์ได้โดยตรง การเพิ่มขั้นทนายในกรณีนี้ไม่ได้ทำให้เอกสารได้รับการยอมรับมากขึ้น ${TAIL}` },
      { q: "ทนาย Notary ไทยเหมือน Notary Public ต่างประเทศไหม", a: `ระบบไทยใช้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารซึ่งขึ้นทะเบียนกับสภาทนายความ ไม่ใช่ตำแหน่ง Notary Public ตามกฎหมายของประเทศอื่น หน้าที่ใกล้เคียงกันในทางปฏิบัติ แต่ควรแจ้งปลายทางตามชื่อที่ถูกต้องเพื่อลดข้อสงสัย ${TAIL}` },
      { q: "ลงนามไปแล้วให้ทนายรับรองย้อนหลังได้ไหม", a: `ไม่ได้ เพราะสาระสำคัญคือการลงนามต่อหน้าผู้รับรอง ถ้าลงนามมาก่อนต้องจัดทำฉบับใหม่และลงนามต่อหน้าอีกครั้ง การนำเอกสารที่ลงนามล่วงหน้ามาให้รับรองย้อนหลังเป็นเหตุให้ปลายทางปฏิเสธได้ ${TAIL}` },
      { q: "ผู้ลงนามอยู่ต่างประเทศทำอย่างไร", a: `ลงนามต่อหน้าเจ้าหน้าที่ที่ปลายทางยอมรับ เช่น สถานเอกอัครราชทูตหรือสถานกงสุลไทยในประเทศนั้น แล้วจึงนำเอกสารกลับเข้าสู่ขั้นรับรองที่เหลือ ควรยืนยันรูปแบบกับผู้รับเอกสารก่อนดำเนินการ ${TAIL}` },
    ],
    synonyms: ["notary กับ นิติกรณ์", "notary vs mfa legalisation", "รับรองลายมือชื่อก่อนกงสุล"],
  },
  {
    slug: "wait-2027-or-legalise-now",
    title: "รอ Apostille ปี 2027 หรือทำนิติกรณ์เลยดีกว่า",
    description:
      "เทียบผลของการรอให้อนุสัญญา Apostille มีผลกับไทย กับการดำเนินการตามเส้นทางนิติกรณ์ปัจจุบัน เพื่อวางแผนวันยื่นให้ทัน",
    h1: "รอ Apostille 2027 vs ทำนิติกรณ์ตอนนี้",
    aLabel: "รอ Apostille",
    bLabel: "ทำนิติกรณ์ตอนนี้",
    answer: `ถ้ามีกำหนดยื่นก่อนวันที่ ${APOSTILLE_STATUS.inForceDateTh} ต้องใช้เส้นทางนิติกรณ์ปัจจุบัน เพราะยังไม่มีตรา Apostille ของไทยให้ขอ การรอมีประโยชน์เฉพาะกรณีที่วันยื่นอยู่หลังวันดังกล่าวและปลายทางเป็นประเทศภาคีอนุสัญญา`,
    rows: [
      { aspect: "ใช้ได้เมื่อไหร่", a: `ตั้งแต่ ${APOSTILLE_STATUS.inForceDateTh}`, b: "ใช้ได้ทันที" },
      { aspect: "เหมาะกับ", a: "แผนระยะยาวที่ยืดหยุ่นวันยื่นได้", b: "กำหนดนัดที่แน่นอนแล้ว" },
      { aspect: "ความเสี่ยง", a: "พลาดกำหนดนัดของสถานทูตหรือหน่วยงานปลายทาง", b: "มีขั้นตอนมากกว่าและใช้เวลานานกว่า" },
      { aspect: "งานที่ทำล่วงหน้าได้", a: "คัดต้นฉบับและแปล", b: "คัดต้นฉบับและแปลเช่นกัน" },
      { aspect: "ค่าธรรมเนียม", a: "ยังไม่มีการประกาศอัตราสำหรับไทย", b: "200/400 บาทต่อตราประทับ + ค่าธรรมเนียมสถานทูต" },
      { aspect: "ประเทศนอกภาคี", a: "ยังต้องใช้เส้นทางเดิมอยู่ดี", b: "ครอบคลุมอยู่แล้ว" },
    ],
    chooseA: [
      `วันยื่นจริงอยู่หลัง ${APOSTILLE_STATUS.inForceDateTh}`,
      "ปลายทางเป็นประเทศภาคีอนุสัญญากรุงเฮก",
      "ไม่มีกำหนดนัดที่ผูกมัดในระหว่างนี้",
    ],
    chooseB: [
      "มีกำหนดนัดยื่นวีซ่า จดทะเบียน หรือขึ้นศาลในปีนี้",
      "ปลายทางเป็นประเทศนอกภาคีอนุสัญญา",
      "หน่วยงานปลายทางระบุตราสถานทูตไว้ในเช็กลิสต์",
    ],
    faqs: [
      { q: "เตรียมอะไรไว้ก่อนได้บ้างโดยไม่เสียเปล่า", a: `คัดต้นฉบับจากหน่วยงานผู้ออกและจัดทำคำแปลที่สะกดตรงพาสปอร์ต สองขั้นนี้ใช้ได้กับทั้งเส้นทางปัจจุบันและเส้นทาง Apostille ในอนาคต แต่ระวังข้อกำหนดอายุเอกสาร 3-6 เดือนของปลายทาง ${TAIL}` },
      { q: "เอกสารที่รับรองแล้วจะยังใช้ได้หลังอนุสัญญามีผลไหม", a: `การรับรองที่ทำถูกต้องตามเส้นทางที่บังคับใช้ในขณะนั้นไม่ได้เสียไปเพราะอนุสัญญามีผล แต่หน่วยงานปลายทางอาจกำหนดอายุเอกสารของตนเอง จึงควรยืนยันกับผู้รับเอกสารก่อนยื่น ${TAIL}` },
      { q: "จะรู้ได้อย่างไรว่าปลายทางเป็นภาคีอนุสัญญา", a: `ตรวจรายชื่อภาคีจากเว็บไซต์ทางการของ HCCH (hcch.net) ซึ่งเป็นผู้ดูแลอนุสัญญา และยืนยันกับหน่วยงานผู้รับเอกสารอีกครั้ง เพราะบางหน่วยงานมีข้อกำหนดภายในเพิ่มเติมนอกเหนือจากอนุสัญญา ${TAIL}` },
      { q: "ระหว่างนี้ควรวางแผนเวลาอย่างไร", a: `เผื่อเวลารวมประมาณ 1-3 สัปดาห์สำหรับแปล นิติกรณ์ และตราสถานทูต โดยระยะเวลาจริงขึ้นกับคิวของแต่ละหน่วยงานและจำนวนฉบับ ${FEE} ${QUOTE} ${TAIL}` },
    ],
    synonyms: ["รอ apostille ไทยดีไหม", "wait for apostille thailand 2027", "legalise now or wait"],
  },
];

export const APOSTILLE_COMPARE_BY_SLUG: Record<string, ApostilleCompare> = Object.fromEntries(
  APOSTILLE_COMPARE.map((c) => [c.slug, c]),
);
